import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Notification, NotificationKind } from '@/api/notifications';

// Human-readable copy for each kind. Source of truth for both the panel
// row + future push notification copy. Subtitle is built per-row from
// the notification's payload, which is shape-loose by design (kind-
// specific keys).
const KIND_LABELS: Record<NotificationKind, { headline: string }> = {
  agreement_signed:    { headline: 'Agreement signed' },
  agreement_unsigned:  { headline: 'Agreement awaiting signature' },
  booking_request:     { headline: 'New booking request' },
  repair_request:      { headline: 'Fix list received' },
  payment_received:    { headline: 'Payment received' },
  report_published:    { headline: 'Report delivered' },
  appointment_upcoming:{ headline: 'Upcoming inspection' },
};

function buildSubtitle(n: Notification): string {
  const p = (n.payload ?? {}) as Record<string, unknown>;
  const clientName = typeof p.clientName === 'string' ? p.clientName : undefined;
  const propertyAddress = typeof p.propertyAddress === 'string' ? p.propertyAddress : undefined;
  const senderName = typeof p.senderName === 'string' ? p.senderName : undefined;
  switch (n.kind) {
    case 'agreement_signed':
      return clientName ? `${clientName} signed the agreement` : 'Client signed the agreement';
    case 'agreement_unsigned':
      return clientName
        ? `${clientName} hasn't signed yet`
        : 'Awaiting client signature';
    case 'booking_request':
      return propertyAddress
        ? `${clientName ?? 'A client'} · ${propertyAddress}`
        : (clientName ?? 'A new booking');
    case 'repair_request':
      return senderName
        ? `${senderName} sent a fix list${propertyAddress ? ` · ${propertyAddress}` : ''}`
        : (propertyAddress ?? 'New fix list');
    case 'payment_received':
      return propertyAddress
        ? `Payment received · ${propertyAddress}`
        : 'Payment received';
    case 'report_published':
      return propertyAddress ?? (clientName ?? 'Report delivered');
    case 'appointment_upcoming':
      return propertyAddress ?? (clientName ?? 'Upcoming inspection');
  }
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 60_000) return 'just now';
  const mins = Math.round(diffMs / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

interface NotificationsPanelProps {
  notifications: Notification[];
  loading: boolean;
  unreadCount: number;
  onClose: () => void;
  /** Mark a single notification as read on the server. Called when the user
   *  taps a row before we navigate. */
  onMarkRead: (id: string) => Promise<void> | void;
  /** Mark every unread notification as read. */
  onMarkAllRead: () => Promise<void> | void;
}

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  notifications,
  loading,
  unreadCount,
  onClose,
  onMarkRead,
  onMarkAllRead,
}) => {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  // Outside click — uses mousedown so it fires before the click target sees it.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSelect = (n: Notification) => {
    // Optimistic mark-read; the server call is best-effort and the parent
    // refetch will reconcile if it fails.
    if (!n.readAt) void onMarkRead(n.id);
    if (n.linkPath) navigate(n.linkPath);
    onClose();
  };

  const isEmpty = notifications.length === 0;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 bg-surface-1 border border-border-subtle rounded-xl shadow-2xl z-50 overflow-hidden"
      style={{ minWidth: '300px' }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <p className="text-heading-sm text-ink font-medium">Notifications</p>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => void onMarkAllRead()}
            className="text-caption text-ink-subtle hover:text-ink underline-offset-2 hover:underline"
          >
            Mark all read
          </button>
        )}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {loading && isEmpty ? (
          <div className="px-4 py-4 text-body-sm text-ink-subtle text-center">Loading…</div>
        ) : isEmpty ? (
          <div className="px-4 py-8 text-center">
            <p className="text-body-sm text-ink-subtle">All caught up</p>
          </div>
        ) : (
          <ul>
            {notifications.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(n)}
                  className={
                    'w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors ' +
                    (n.readAt
                      ? 'hover:bg-surface-2'
                      : 'bg-brand-soft/30 hover:bg-brand-soft/50')
                  }
                >
                  <span
                    className={
                      'shrink-0 mt-1.5 w-2 h-2 rounded-full ' +
                      (n.readAt ? 'bg-transparent' : 'bg-brand')
                    }
                    aria-label={n.readAt ? undefined : 'Unread'}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-body-sm font-medium text-ink truncate">
                        {KIND_LABELS[n.kind].headline}
                      </p>
                      <span className="text-caption text-ink-faint shrink-0 tabular-nums">
                        {relativeTime(n.createdAt)}
                      </span>
                    </div>
                    <p className="text-caption text-ink-muted truncate">
                      {buildSubtitle(n)}
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
